import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import { Plus, Calendar, MapPin, Phone } from "lucide-react";
import ReferralCard from "../components/ReferralCard";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  confirmed: "bg-blue-100 text-blue-800 border-blue-300",
  in_progress: "bg-indigo-100 text-indigo-800 border-indigo-300",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-300",
  cancelled: "bg-rose-100 text-rose-800 border-rose-300"
};

export default function Dashboard() {
  const { user, loading } = useAuth();
  const { t } = useLang();
  const [bookings, setBookings] = useState([]);
  const [fetching, setFetching] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await api.get("/bookings/me");
        setBookings(r.data);
      } finally { setFetching(false); }
    })();
  }, [user]);

  if (loading || !user) {
    return <div className="min-h-[60vh] flex items-center justify-center text-neutral-500">{t.common.loading}</div>;
  }

  let content;
  if (fetching) {
    content = <div className="text-neutral-500">{t.common.loading}</div>;
  } else if (bookings.length === 0) {
    content = (
      <div className="border border-dashed border-neutral-300 p-16 text-center bg-white" data-testid="dashboard-empty">
        <p className="text-neutral-600 mb-6">{t.dashboard.empty}</p>
        <Link to="/book" className="btn-primary inline-flex">{t.dashboard.book_now}</Link>
      </div>
    );
  } else {
    content = (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {bookings.map(b => (
          <div key={b.booking_id} className="bg-white border border-neutral-200 p-6 card-lift" data-testid={`booking-card-${b.booking_id}`}>
            <div className="flex items-start justify-between mb-4">
              <span className="text-xs font-bold tracking-widest uppercase text-neutral-500">{b.booking_id.slice(0, 10)}</span>
              <span className={`text-xs font-bold px-2 py-1 border rounded-sm ${STATUS_COLORS[b.status]}`}>
                {t.dashboard.status[b.status]}
              </span>
            </div>
            <h3 className="font-bold text-lg tracking-tight mb-3">{t.services[b.service]?.name || t.specialty[b.service]?.name || b.service}</h3>
            <div className="space-y-2 text-sm text-neutral-600">
              <div className="flex items-center gap-2"><Calendar size={14} /> {b.date} · {b.time}</div>
              <div className="flex items-center gap-2"><MapPin size={14} /> {b.address}</div>
              <div className="flex items-center gap-2"><Phone size={14} /> {b.phone}</div>
            </div>
            {b.details && <p className="text-xs text-neutral-500 mt-3 border-t border-neutral-100 pt-3">{b.details}</p>}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 lg:py-16">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-2">{user.name}</p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter" data-testid="dashboard-title">{t.dashboard.title}</h1>
        </div>
        <Link to="/book" className="btn-primary" data-testid="dashboard-new-booking-btn">
          <Plus size={18} /> {t.dashboard.new_booking}
        </Link>
      </div>

      <div className="mb-10">
        <ReferralCard />
      </div>

      {content}
    </div>
  );
}
